import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import SaveToLibraryButton from '../../components/SaveToLibraryButton';
import LibraryFreeNote from '../../components/LibraryFreeNote';
import {
  Search, FileText, ExternalLink, Download, Printer, Mail, Copy, Bookmark, ShieldAlert,
  MapPin, X, Loader2, ScrollText,
} from 'lucide-react';

const LOGO_URL = '/inasw-logo-2026.jpg';

const YesNo = ({ label, value }) => (
  <span className="text-xs text-gray-600"><strong className="text-gray-700">{label}:</strong> {value === true ? 'Yes' : value === false ? 'No' : '—'}</span>
);

const StatusBadge = ({ status }) => {
  const map = {
    'Current': 'bg-green-100 text-green-800',
    'Under Review': 'bg-amber-100 text-amber-800',
    'Replaced': 'bg-orange-100 text-orange-800',
    'Archived': 'bg-gray-200 text-gray-600',
    'Link Unavailable': 'bg-red-100 text-red-700',
  };
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${map[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>;
};

export const FormCard = ({ f, onSaveToggle, saved }) => {
  const { isAuthenticated } = useAuth();
  const [preview, setPreview] = useState(false);
  const link = f.pdf_url || f.official_url;

  const copyLink = () => { navigator.clipboard.writeText(link || ''); toast.success('Official link copied.'); };
  const print = () => { if (link) window.open(link, '_blank'); };
  const email = async () => {
    if (!isAuthenticated) { toast.info('Sign in to email forms.'); return; }
    const to = window.prompt('Send this official form link to which email?');
    if (!to) return;
    try {
      await axios.post(`${API_URL}/legal-forms/packet/email`, { form_ids: [f.form_id], recipient_email: to });
      toast.success(`Sent to ${to}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send email.'); }
  };

  return (
    <div className="bg-white rounded-xl border p-5 flex flex-col" data-testid={`form-card-${f.form_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="inline-flex items-center gap-1 text-xs text-secondary font-semibold"><MapPin className="w-3 h-3" /> {f.state}</span>
          <h3 className="font-serif text-lg font-bold text-primary leading-snug mt-0.5">{f.display_title}</h3>
          <p className="text-xs text-gray-500">{f.category_label}</p>
        </div>
        <StatusBadge status={f.status} />
      </div>
      {f.description && <p className="text-sm text-gray-600 mt-2">{f.description}</p>}
      {f.warning && (
        <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2 flex gap-2 text-xs text-amber-800" data-testid={`form-warning-${f.form_id}`}>
          <span className="font-bold">⚠</span><span>{f.warning}</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-3">
        <YesNo label="Witness" value={f.witness_required} />
        <YesNo label="Notarization" value={f.notarization_required} />
        <YesNo label="Clinician signature" value={f.clinician_signature_required} />
        <span className="text-xs text-gray-600"><strong className="text-gray-700">Language:</strong> {f.language}</span>
      </div>
      <div className="text-xs text-gray-500 mt-3 border-t pt-2">
        <p><strong>Official source:</strong> {f.official_source || '—'}</p>
        <p><strong>Last verified:</strong> {f.last_verified ? new Date(f.last_verified).toLocaleDateString() : '—'}</p>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {link && <Button size="sm" onClick={() => setPreview(true)} className="bg-primary hover:bg-primary/90 text-white" data-testid={`view-form-${f.form_id}`}><FileText className="w-3.5 h-3.5 mr-1" /> View Official Form</Button>}
        {link && <a href={link} target="_blank" rel="noreferrer" download><Button size="sm" variant="outline" data-testid={`download-form-${f.form_id}`}><Download className="w-3.5 h-3.5 mr-1" /> Download</Button></a>}
        {link && <Button size="sm" variant="outline" onClick={print}><Printer className="w-3.5 h-3.5 mr-1" /> Print</Button>}
        <Button size="sm" variant="outline" onClick={email}><Mail className="w-3.5 h-3.5 mr-1" /> Email</Button>
        {link && <Button size="sm" variant="outline" onClick={copyLink}><Copy className="w-3.5 h-3.5 mr-1" /> Copy Link</Button>}
        {onSaveToggle && <Button size="sm" variant="outline" onClick={() => onSaveToggle(f.form_id)} className={saved ? 'text-secondary border-secondary' : ''}><Bookmark className="w-3.5 h-3.5 mr-1" /> {saved ? 'Saved' : 'Save'}</Button>}
        <SaveToLibraryButton item={{ item_type: 'legal_form', ref_id: f.form_id, title: f.display_title, subtitle: f.state, category: f.category_label, url: link || '' }} />
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPreview(false)} data-testid="form-preview-modal">
          <div className="bg-white rounded-xl w-full max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b">
              <p className="font-semibold text-primary text-sm truncate">{f.display_title} — {f.state}</p>
              <div className="flex items-center gap-2">
                <a href={link} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Open in new tab</a>
                <button onClick={() => setPreview(false)} data-testid="close-preview"><X className="w-5 h-5 text-gray-500" /></button>
              </div>
            </div>
            <iframe title="form-preview" src={link} className="flex-1 w-full rounded-b-xl" />
            <div className="p-2 text-center text-xs text-gray-400 border-t">If the preview does not load, use "Open in new tab" — some official sites block embedding.</div>
          </div>
        </div>
      )}
    </div>
  );
};

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="block text-xs font-semibold text-white/80 mb-1">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white text-gray-900 rounded-md px-3 h-10 text-sm border" data-testid={testid}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const LegalFormsDatabase = () => {
  const { isAuthenticated } = useAuth();
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({ state: '', category: '', language: '', adult_or_minor: '', witness_required: '', notarization_required: '', electronic_registry: '' });
  const [forms, setForms] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savedIds, setSavedIds] = useState([]);
  const [flyerOpen, setFlyerOpen] = useState(false);

  const printFlyer = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>INASW Advance Directives Flyer</title></head><body style="margin:0"><img src="${window.location.origin}/advance-directives-flyer.png" style="width:100%;display:block" onload="window.print()"/></body></html>`);
    w.document.close();
  };

  useEffect(() => {
    document.title = 'National Social Work Advance Directives & Legal Forms Database | I Need A Social Worker';
    axios.get(`${API_URL}/legal-forms/meta`).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
      const r = await axios.get(`${API_URL}/legal-forms?${q.toString()}`);
      setForms(r.data.forms || []); setTotal(r.data.total || 0);
    } catch (e) { setForms([]); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API_URL}/legal-forms/saved/list`).then((r) => setSavedIds((r.data.forms || []).map((f) => f.form_id))).catch(() => {});
  }, [isAuthenticated]);

  const saveToggle = async (formId) => {
    if (!isAuthenticated) { toast.info('Sign in to save forms to your dashboard.'); return; }
    try {
      const r = await axios.post(`${API_URL}/legal-forms/save`, { form_id: formId });
      setSavedIds((prev) => r.data.saved ? [...prev, formId] : prev.filter((x) => x !== formId));
    } catch (e) { toast.error('Could not save.'); }
  };

  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));
  const yn = [{ value: '', label: 'Any' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      {/* Hero */}
      <section className="bg-primary pt-24 pb-10 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <img src={LOGO_URL} alt="INASW" className="w-16 h-16 rounded-full object-cover ring-2 ring-secondary mb-4" />
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">National Social Work <span className="text-secondary">Advance Directives</span> & Legal Forms Database<sup className="text-secondary text-[0.4em] align-super font-semibold ml-0.5">™</sup></h1>
            <p className="mt-3 text-white/85 max-w-xl">Trusted. Accurate. State-specific. Find official advance-directive forms and requirements for all 50 states and Washington, D.C. — no guessing, no typing state names.</p>
            {meta?.states_total && (
              <div className="mt-5 inline-flex items-center gap-3 bg-white/10 border border-white/20 rounded-full pl-2 pr-5 py-2" data-testid="coverage-counter">
                <span className="bg-secondary text-white font-bold rounded-full px-3 py-1 text-sm">{meta.states_covered} / {meta.states_total}</span>
                <span className="text-sm text-white/90">states &amp; DC available — <span className="text-secondary font-semibold">more added weekly</span></span>
              </div>
            )}
          </div>
          <div className="hidden lg:block">
            <button type="button" onClick={() => setFlyerOpen(true)} className="group relative block w-full max-w-md mx-auto cursor-zoom-in" data-testid="flyer-hero-button" aria-label="Enlarge and print the Advance Directives flyer">
              <img src="/advance-directives-flyer.png" alt="Advance Directives for your state" className="rounded-xl shadow-2xl w-full block" data-testid="flyer-hero-image" />
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs font-semibold px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">Click to enlarge &amp; print</span>
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-5">
        <LibraryFreeNote variant="banner" />
      </div>

      {flyerOpen && (
        <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setFlyerOpen(false)} data-testid="flyer-lightbox">
          <div className="relative max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end gap-2 mb-2">
              <Button size="sm" onClick={printFlyer} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="flyer-print-btn"><Printer className="w-4 h-4 mr-1" /> Print</Button>
              <a href="/advance-directives-flyer.png" download className="inline-flex"><Button size="sm" variant="outline" className="bg-white" data-testid="flyer-download-btn"><Download className="w-4 h-4 mr-1" /> Download</Button></a>
              <Button size="sm" variant="outline" className="bg-white" onClick={() => setFlyerOpen(false)} data-testid="flyer-close-btn"><X className="w-4 h-4" /></Button>
            </div>
            <img src="/advance-directives-flyer.png" alt="Advance Directives flyer — full size" className="w-full rounded-lg shadow-2xl bg-white" />
          </div>
        </div>
      )}

      {/* Search filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-primary/95 rounded-2xl p-5 shadow-lg" data-testid="legal-forms-search">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SelectField label="State" value={filters.state} onChange={(v) => setF('state', v)} testid="filter-state"
              options={[{ value: '', label: 'All states' }, ...(meta?.states || []).map((s) => ({ value: s.slug, label: s.name }))]} />
            <SelectField label="Document type" value={filters.category} onChange={(v) => setF('category', v)} testid="filter-category"
              options={[{ value: '', label: 'All documents' }, ...(meta?.categories || []).map((c) => ({ value: c.slug, label: c.label }))]} />
            <SelectField label="Language" value={filters.language} onChange={(v) => setF('language', v)} testid="filter-language"
              options={[{ value: '', label: 'Any language' }, ...(meta?.languages || []).map((l) => ({ value: l, label: l }))]} />
            <SelectField label="Adult or minor" value={filters.adult_or_minor} onChange={(v) => setF('adult_or_minor', v)} testid="filter-adult-minor"
              options={[{ value: '', label: 'Any' }, { value: 'Adult', label: 'Adult' }, { value: 'Minor', label: 'Minor' }]} />
            <SelectField label="Witness required" value={filters.witness_required} onChange={(v) => setF('witness_required', v)} testid="filter-witness" options={yn} />
            <SelectField label="Notarization required" value={filters.notarization_required} onChange={(v) => setF('notarization_required', v)} testid="filter-notarization" options={yn} />
            <SelectField label="Electronic registry" value={filters.electronic_registry} onChange={(v) => setF('electronic_registry', v)} testid="filter-registry" options={yn} />
            <div className="flex items-end">
              <Button onClick={fetchForms} className="w-full bg-secondary hover:bg-secondary-hover text-white h-10" data-testid="legal-search-btn"><Search className="w-4 h-4 mr-2" /> Search</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-xl font-bold text-primary">{total} official {total === 1 ? 'form' : 'forms'}</h2>
          {isAuthenticated && <Link to="/my-legal-forms" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="my-saved-forms-link"><Bookmark className="w-4 h-4" /> My saved forms</Link>}
        </div>
        {loading ? (
          <div className="text-center py-16 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : forms.length === 0 ? (
          <div className="bg-white rounded-xl border p-10 text-center">
            <ScrollText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-600 font-medium">No official forms match your filters yet.</p>
            <p className="text-gray-400 text-sm mt-1">We only publish official, verified forms. More states are being added — try a broader search or browse by state.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {forms.map((f) => <FormCard key={f.form_id} f={f} onSaveToggle={saveToggle} saved={savedIds.includes(f.form_id)} />)}
          </div>
        )}
      </section>

      {/* Browse by state */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <h2 className="font-serif text-xl font-bold text-primary mb-3">Browse by state</h2>
        <div className="flex flex-wrap gap-2">
          {(meta?.states || []).map((s) => (
            <Link key={s.slug} to={`/advance-directives/${s.slug}`} className="px-3 py-1.5 rounded-full bg-white border text-sm text-gray-700 hover:border-secondary hover:text-primary" data-testid={`state-chip-${s.slug}`}>{s.name}</Link>
          ))}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-sm text-amber-800" data-testid="legal-disclaimer">
          <ShieldAlert className="w-5 h-5 flex-shrink-0 text-amber-600" />
          <p>{meta?.disclaimer}</p>
        </div>
      </section>
    </div>
  );
};

export default LegalFormsDatabase;
